import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { LegalFooter } from "@/components/LegalFooter";

const NAVY = "#13224A";
const DOCS = {
  termos: { file: "termos", pair: "/en/terms" }, privacidade: { file: "privacidade", pair: "/en/privacy" }, seguranca: { file: "seguranca", pair: "/en/security" },
  "en-terms": { file: "en-terms", pair: "/termos", en: true }, "en-privacy": { file: "en-privacy", pair: "/privacidade", en: true },
  "en-security": { file: "en-security", pair: "/seguranca", en: true },
};
const TOKEN = /(\*\*[^*]+\*\*|\[[^\]]+\]|\{[A-Z_]+\}|imodesk\.pt\/(?:en\/)?[a-z]+|www\.cnpd\.pt|[a-z]+@imodesk\.pt)/g;

function Inline({ text }) {
  return text.split(TOKEN).filter(Boolean).map((s, i) => {
    if (s.startsWith("**")) return <b key={i} className="text-slate-800">{s.slice(2, -2)}</b>;
    if (s.startsWith("[") || s.startsWith("{")) return <mark key={i} className="bg-amber-100 text-amber-900 rounded px-1" data-testid="legal-placeholder">{s}</mark>;
    if (s.startsWith("imodesk.pt/")) return <Link key={i} to={s.slice(10)} className="underline underline-offset-2" style={{ color: NAVY }}>{s}</Link>;
    if (s === "www.cnpd.pt") return <a key={i} href="https://www.cnpd.pt" target="_blank" rel="noreferrer" className="underline underline-offset-2" style={{ color: NAVY }}>{s}</a>;
    if (s.endsWith("@imodesk.pt")) return <a key={i} href={`mailto:${s}`} className="underline underline-offset-2" style={{ color: NAVY }}>{s}</a>;
    return s;
  });
}

// Texto em markdown simples: **título de secção**, listas «- », tabelas «|» e parágrafos.
function parse(md) {
  const lines = md.split("\n");
  const title = lines[0].replace(/\*\*/g, "");
  const updated = lines[1];
  const blocks = [];
  for (const ln of lines.slice(2)) {
    const last = blocks[blocks.length - 1];
    if (!ln.trim()) continue;
    if (/^\*\*[^*]+\*\*$/.test(ln.trim())) blocks.push({ t: "h", text: ln.trim().slice(2, -2) });
    else if (ln.startsWith("- ")) last?.t === "ul" ? last.items.push(ln.slice(2)) : blocks.push({ t: "ul", items: [ln.slice(2)] });
    else if (ln.startsWith("|")) {
      if (/^\|[\s|-]+\|$/.test(ln)) continue;
      const cells = ln.split("|").slice(1, -1).map((c) => c.trim());
      last?.t === "table" ? last.rows.push(cells) : blocks.push({ t: "table", head: cells, rows: [] });
    } else blocks.push({ t: "p", text: ln });
  }
  return { title, updated, blocks };
}

function Table({ b }) {
  return (
    <>
      <table className="hidden sm:table w-full text-[15px] border-collapse" data-testid="legal-table">
        <thead><tr className="text-left" style={{ color: NAVY }}>{b.head.map((h) => <th key={h} className="py-2.5 px-3 border-b-2 border-slate-200 font-bold">{h}</th>)}</tr></thead>
        <tbody>{b.rows.map((r) => <tr key={r[0]} className="align-top">{r.map((x, i) => <td key={i} className="py-3 px-3 border-b border-slate-100 text-slate-600"><Inline text={x} /></td>)}</tr>)}</tbody>
      </table>
      <div className="sm:hidden space-y-3" data-testid="legal-table-mobile">
        {b.rows.map((r) => (
          <div key={r[0]} className="rounded-xl border border-slate-200 p-4 text-[15px]">
            <div className="font-bold" style={{ color: NAVY }}><Inline text={r[0]} /></div>
            {r.slice(1).map((x, i) => <div key={i} className="mt-1.5 text-slate-600"><span className="text-xs font-semibold uppercase tracking-wide text-slate-400 block">{b.head[i + 1]}</span><Inline text={x} /></div>)}
          </div>
        ))}
      </div>
    </>
  );
}

function Block({ b }) {
  if (b.t === "h") return <h2 className="pt-6 text-lg font-bold" style={{ color: NAVY }}>{b.text}</h2>;
  if (b.t === "ul") return <ul className="list-disc pl-5 space-y-2 text-slate-600 leading-relaxed">{b.items.map((x) => <li key={x}><Inline text={x} /></li>)}</ul>;
  if (b.t === "table") return <Table b={b} />;
  return <p className="text-slate-600 leading-relaxed"><Inline text={b.text} /></p>;
}

function LangSwitch({ en, pair, here }) {
  const cls = (on) => `px-2.5 py-1 rounded-md transition-colors ${on ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`;
  return (
    <div className="inline-flex items-center gap-1 text-sm font-semibold border border-slate-200 rounded-lg p-0.5" data-testid="legal-lang">
      <Link to={en ? pair : here} className={cls(!en)} data-testid="legal-lang-pt">PT</Link>
      <Link to={en ? here : pair} className={cls(en)} data-testid="legal-lang-en">EN</Link>
    </div>
  );
}

export default function Legal({ doc }) {
  const d = DOCS[doc];
  const [md, setMd] = useState(null);
  useEffect(() => { setMd(null); fetch(`/legal/${d.file}.md`).then((r) => r.text()).then(setMd); window.scrollTo(0, 0); }, [d.file]);
  const p = md ? parse(md) : null;
  useEffect(() => {
    if (p) document.title = `${p.title} · ImoDesk`;
    document.documentElement.lang = d.en ? "en" : "pt-PT";
    return () => { document.documentElement.lang = "pt-PT"; };
  }, [p?.title, d.en]); // eslint-disable-line react-hooks/exhaustive-deps
  const here = window.location.pathname;
  return (
    <div className="min-h-screen bg-white font-body" data-testid={`legal-page-${doc}`} lang={d.en ? "en" : "pt-PT"}>
      <div className="mx-auto max-w-[760px] px-5 sm:px-8 py-8 sm:py-12 text-base">
        <nav className="flex items-center justify-between gap-3">
          <Link to="/" data-testid="legal-home"><Logo size={30} /></Link>
          <div className="flex items-center gap-4">
            <LangSwitch en={d.en} pair={d.pair} here={here} />
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: NAVY }} data-testid="legal-back"><ArrowLeft size={15} /> {d.en ? "Back" : "Voltar"}</Link>
          </div>
        </nav>
        {d.en && <p className="mt-8 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5 text-sm text-slate-600" data-testid="legal-translation-note">This is a translation. In case of discrepancy, the Portuguese version prevails.</p>}
        {p && (
          <article>
            <h1 className="mt-10 font-extrabold tracking-tight text-4xl sm:text-5xl" style={{ color: NAVY }} data-testid="legal-title">{p.title}</h1>
            <p className="mt-3 text-sm text-slate-500" data-testid="legal-updated"><Inline text={p.updated} /></p>
            <div className="mt-6 space-y-4" data-testid="legal-body">{p.blocks.map((b, i) => <Block key={i} b={b} />)}</div>
          </article>
        )}
        <LegalFooter en={d.en} className="mt-14 pt-6 border-t border-slate-100" />
      </div>
    </div>
  );
}
